import { AnyAction } from 'redux';
import { Operation } from '@apollo/client';
import { match } from 'react-router';
import { Location } from 'history';
import UAParser from 'ua-parser-js';

import { attributePrefix } from './options';

declare global {
  interface Window {
    screen?: { height: number; width: number };
  }
}

const userAgentParser = new UAParser();
const parsedUserAgent = userAgentParser.getResult();

const { devicePixelRatio: windowDpr, screen: windowScreen } = window;

export const getBaseFontSize = () => getComputedStyle(document.documentElement).fontSize;
export const baseFontSize = getBaseFontSize();

// @NOTE-RT: Have this integration point in here for now, just in case we need to add more common, hard-coded details
export const buildEventDetails = (details?: Record<string, any>) => details;

export const buildReduxActionEventDetails = (action: AnyAction, supplementaryDetails = {}) =>
  buildEventDetails({
    label: action.type,
    ...supplementaryDetails,
    type: action.type,
    meta: action.meta,
    nonInteraction: 1,
  });

export const buildApolloOperationEventDetails = (operation: Operation, supplementaryDetails = {}) =>
  buildEventDetails({
    label: operation.operationName,
    ...supplementaryDetails,
    operationName: operation.operationName,
    operationType: operation.query ? 'query' : 'mutation',
    variables: operation.variables,
    nonInteraction: 1,
  });

export const customParams = {
  browser: parsedUserAgent.browser.name,
  browser_version: parsedUserAgent.browser.version,
};

export const defaultContext = {
  app: {
    name: 'organizer',
  },
  device: {
    type: parsedUserAgent.device.type,
    model: parsedUserAgent.device.model,
    manufacturer: parsedUserAgent.device.vendor,
  },
  os: parsedUserAgent.os,
  screen: {
    density: windowDpr,
    height: windowScreen?.height,
    width: windowScreen?.width,
  },
};

export const pageDefaults = (routeState: Location & match) =>
  buildEventDetails({
    value: routeState.pathname,
    pathname: routeState.pathname,
    search: routeState.search,
    hash: routeState.hash,
    params: routeState.params,
    window: {
      height: window.innerHeight,
      width: window.innerWidth,
    },
  });

// @NOTE-RT: Unused for the moment, but this'll come in handy for arbitrarily decorating React components with `data-metrics-*` attributes to capture information on specific clicks
export const transformAnalyticsContextToDataMetrics = (
  analyticsContext: object
): Record<string, any> =>
  analyticsContext
    ? Object.keys(analyticsContext).reduce((acc, val) => {
        acc[`${attributePrefix}-${val}`] = analyticsContext[val];
        return acc;
      }, {})
    : {};
